// src/careJobs/components/jobApplyDialog.tsx
import React, { useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Alert,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import { APPLY_TO_JOB, UPDATE_JOB_APPLICATION } from "../../../../platform/graphql/mutations/careJobs";

// Signed URL flow reused for resumes
import { GENERATE_UPLOAD_URL } from "../../../../platform/graphql/mutations/image";
import { uploadToGCS } from "../../../../platform/util/upload/uploadToGCS";

import { UserContext } from "../../../../platform/components/auth/auth-context";
import SmsConsentCheckbox from "../../../../platform/components/legal/SmsConsentCheckbox";

type AuthUser = {
  id?: string | number | null;
  username?: string | null;
  email?: string | null;
  phone?: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  jobId: number | null;
  jobTitle?: string;

  isAuthed?: boolean;
  currentUser?: AuthUser | null;
  lockIdentityFields?: boolean;

  signInHref?: string;
  signUpHref?: string;
  onAuthClick?: (mode: "signin" | "signup") => void;

  onApplied?: (jobId: number) => void;
};

const ACCEPT_MIME =
  ".pdf,application/pdf,.doc,application/msword,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const MY_APPS_MIN = gql`
  query MyJobAppsMin {
    myJobApplications {
      id
      jobId
      job { id }
      applicantName
      applicantEmail
      applicantPhone
      coverLetter
      resumeUrl
      createdAt
      updatedAt
      status
    }
  }
`;

type ResumeSource = "existing" | "link" | "upload";
type ResumeEntry = { url: string; ts: number };

export default function JobApplyDialog({
  open,
  onClose,
  jobId,
  jobTitle,
  lockIdentityFields = true,
  signInHref = "/login",
  signUpHref = "/signup",
  onAuthClick,
  onApplied,
}: Props) {
  const { providerUser } = useContext(UserContext);

  const [applicantName, setApplicantName] = React.useState("");
  const [applicantEmail, setApplicantEmail] = React.useState("");
  const [applicantPhone, setApplicantPhone] = React.useState("");
  const [smsConsent, setSmsConsent] = React.useState(false);
  const [coverLetter, setCoverLetter] = React.useState("");

  const [resumeUrl, setResumeUrl] = React.useState("");

  const [file, setFile] = React.useState<File | null>(null);
  const [fileUrl, setFileUrl] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);

  const [resumeSource, setResumeSource] = React.useState<ResumeSource>("link");
  const [forceClearResume, setForceClearResume] = React.useState(false);

  const [error, setError] = React.useState<string | null>(null);

  const [submit, { loading: submitting }] = useMutation(APPLY_TO_JOB);
  const [updateApp, { loading: updating }] = useMutation(UPDATE_JOB_APPLICATION);
  const [mutGenerateUploadUrl] = useMutation(GENERATE_UPLOAD_URL);

  const location = useLocation();
  const returnTo = location.pathname + location.search;

  const [existingId, setExistingId] = React.useState<number | null>(null);
  const [duplicateMsg, setDuplicateMsg] = React.useState<string | null>(null);

  const appsQ = useQuery(MY_APPS_MIN, { fetchPolicy: "cache-first", skip: !open });

  const existingForJob = React.useMemo(() => {
    if (!open || !jobId) return null;
    const list = appsQ.data?.myJobApplications ?? [];
    const jId = String(jobId);
    return list.find((a: any) => String(a?.job?.id ?? a?.jobId ?? "") === jId) || null;
  }, [appsQ.data, open, jobId]);

  const mostRecentResumeUrl = React.useMemo<string | null>(() => {
    const list = (appsQ.data?.myJobApplications ?? []) as any[];
    const withResume: ResumeEntry[] = list
      .filter((a: any) => !!a?.resumeUrl)
      .map((a: any): ResumeEntry => ({
        url: String(a.resumeUrl),
        ts: Number(new Date(a.updatedAt || a.createdAt || 0)),
      }));
    if (withResume.length === 0) return null;
    withResume.sort((a: ResumeEntry, b: ResumeEntry) => b.ts - a.ts);
    return withResume[0].url;
  }, [appsQ.data]);

  const savedResumeUrl = React.useMemo(() => {
    return (
      (existingForJob?.resumeUrl && String(existingForJob.resumeUrl)) ||
      (mostRecentResumeUrl && String(mostRecentResumeUrl)) ||
      ""
    );
  }, [existingForJob, mostRecentResumeUrl]);

  // Prefill identity when dialog opens
  React.useEffect(() => {
    if (!open || !providerUser) return;
    setApplicantName((v) => v || (providerUser.username ?? "") || "");
    setApplicantEmail((v) => v || (providerUser.email ?? "") || "");
  }, [open, providerUser]);

  // Enter update mode if we already have an app for this job
  React.useEffect(() => {
    if (!open || !existingForJob) return;
    setExistingId(Number(existingForJob.id));
    setDuplicateMsg("You already applied. Update your application?");
    if (existingForJob.applicantName) setApplicantName(existingForJob.applicantName);
    if (existingForJob.applicantEmail) setApplicantEmail(existingForJob.applicantEmail);
    if (existingForJob.applicantPhone) setApplicantPhone(existingForJob.applicantPhone);
    if (existingForJob.coverLetter) setCoverLetter(existingForJob.coverLetter);
  }, [open, existingForJob]);

  // Default resume source per availability
  React.useEffect(() => {
    if (!open) return;
    setForceClearResume(false);
    setFile(null);
    setFileUrl(null);
    setUploading(false);
    if (savedResumeUrl) {
      setResumeSource("existing");
      setResumeUrl("");
    } else {
      setResumeSource("link");
    }
  }, [open, savedResumeUrl]);

  const reset = () => {
    setApplicantName("");
    setApplicantEmail("");
    setApplicantPhone("");
    setCoverLetter("");
    setResumeUrl("");
    setFile(null);
    setFileUrl(null);
    setUploading(false);
    setSmsConsent(false);
    setError(null);
    setExistingId(null);
    setDuplicateMsg(null);
    setForceClearResume(false);
    setResumeSource("link");
  };

  /* ====================== Upload helpers ====================== */

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (!f) return;
    setError(null);
    setFile(f);
    setFileUrl(null);
    setForceClearResume(false);
    setResumeSource("upload");
    await uploadFile(f); // auto-upload immediately
  };

  const uploadFile = async (fileArg?: File): Promise<string | null> => {
    const effective = fileArg || file;
    if (!effective) return null;
    setError(null);
    setUploading(true);
    try {
      const url = await uploadToGCS(effective, mutGenerateUploadUrl);
      setFileUrl(url);
      return url;
    } catch (err: any) {
      setError(err?.message || "Failed to upload resume.");
      setFileUrl(null);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const removeFileSelection = () => {
    setFile(null);
    setFileUrl(null);
    if (savedResumeUrl) setResumeSource("existing");
    else setResumeSource("link");
  };

  const removeSavedResume = () => {
    setForceClearResume(true);
    if (!fileUrl) setResumeSource("link");
  };

  /* ====================== Submit / Update ====================== */

  const resolveFinalResumeUrl = async (): Promise<string | null | undefined> => {
    if (resumeSource === "existing") {
      if (forceClearResume) return null;
      return savedResumeUrl || null;
    }
    if (resumeSource === "upload") {
      if (uploading) return undefined; // block while uploading
      if (fileUrl) return fileUrl;
      if (file) {
        const url = await uploadFile(file);
        if (!url) return undefined;
        return url;
      }
      return null;
    }
    const t = (resumeUrl || "").trim();
    return t || null;
  };

  const [submitMutate] = React.useState(() => submit);
  const [updateMutate] = React.useState(() => updateApp);

  const handleSubmit = async () => {
    setError(null);
    if (!jobId) return setError("Missing job id.");

    const name = (applicantName || providerUser?.username || "").trim();
    if (!name) return setError("Please enter your name.");
    if (!smsConsent) return setError("Please agree to the SMS consent before submitting your application.");

    try {
      const finalResumeUrl = await resolveFinalResumeUrl();
      if (typeof finalResumeUrl === "undefined") return; // uploading/failed

      const { data } = await submitMutate({
        variables: {
          input: {
            jobId,
            applicantName: name,
            applicantEmail: (applicantEmail || providerUser?.email || "").trim() || null,
            applicantPhone: (applicantPhone || "").trim() || undefined,
            coverLetter: coverLetter.trim() || null,
            resumeUrl: finalResumeUrl,
          },
        },
      });

      const res = (data as any)?.applyToJob ?? (data as any)?.submitJobApplication;
      if (!res?.success) {
        return setError(res?.message || "Failed to submit application.");
      }

      const isDuplicate =
        Boolean(res?.alreadyApplied) ||
        (typeof res?.message === "string" && /already/i.test(res.message) && !!res?.application?.id);

      if (isDuplicate) {
        setExistingId(Number(res.application.id));
        setDuplicateMsg(res.message || "You already applied. Update your application?");
        return;
      }

      onApplied?.(Number(jobId));
      reset();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Network error.");
    }
  };

  const handleUpdate = async () => {
    if (!existingId) return;
    setError(null);
    if (!smsConsent) return setError("Please agree to the SMS consent before updating your application.");

    try {
      const finalResumeUrl = await resolveFinalResumeUrl();
      if (typeof finalResumeUrl === "undefined") return;

      const { data } = await updateMutate({
        variables: {
          patch: {
            id: existingId,
            applicantName: (applicantName || providerUser?.username || "").trim() || undefined,
            applicantEmail: (applicantEmail || providerUser?.email || "").trim() || undefined,
            applicantPhone: (applicantPhone || "").trim() || undefined,
            coverLetter: coverLetter.trim() || undefined,
            resumeUrl: resumeSource === "existing" && forceClearResume ? null : finalResumeUrl ?? undefined,
          },
        },
      });

      const res = (data as any)?.updateJobApplication;
      if (!res?.success) {
        return setError(res?.message || "Failed to update application.");
      }

      onApplied?.(Number(jobId));
      reset();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Network error.");
    }
  };

  const loading = submitting || updating || uploading;

  // Block submit when uploading or file not finished uploading
  const submitBlocked =
    loading || !jobId || (resumeSource === "upload" && (uploading || (!!file && !fileUrl)));

  const showSavedRow = Boolean(savedResumeUrl) && !forceClearResume && resumeSource === "existing";
  const showUploadRow = resumeSource === "upload";

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {duplicateMsg ? "Update your application" : `Apply — ${jobTitle || "Job"}`}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {(uploading || submitting || updating) && <LinearProgress />}

          {!providerUser && !duplicateMsg && (
            <Alert severity="info">
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="body2" sx={{ mr: 0.5 }}>
                  Applying as a guest. <strong>Sign in</strong> for a faster experience:
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    component={RouterLink}
                    to={{ pathname: signInHref, search: `?returnTo=${encodeURIComponent(returnTo)}` }}
                    state={{ returnTo, reopenApplyForJobId: jobId ?? undefined }}
                  >
                    Sign in
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    component={RouterLink}
                    to={{ pathname: signUpHref, search: `?returnTo=${encodeURIComponent(returnTo)}` }}
                    state={{ returnTo, reopenApplyForJobId: jobId ?? undefined }}
                  >
                    Create account
                  </Button>
                </Stack>
              </Stack>
            </Alert>
          )}

          {providerUser && !duplicateMsg && (
            <Alert severity="success">
              Applying as <strong>{providerUser.username || "signed user"}</strong>
              {providerUser.email ? ` (${providerUser.email})` : ""}.
            </Alert>
          )}

          {duplicateMsg && <Alert severity="info">{duplicateMsg}</Alert>}

          <TextField
            label="Full name *"
            value={applicantName}
            onChange={(e) => setApplicantName(e.target.value)}
            autoFocus
            disabled={!!providerUser?.username && lockIdentityFields}
            placeholder={providerUser?.username || ""}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Email"
              value={applicantEmail}
              onChange={(e) => setApplicantEmail(e.target.value)}
              sx={{ flex: 1 }}
              disabled={!!providerUser?.email && lockIdentityFields}
              placeholder={providerUser?.email || ""}
              type="email"
            />
            <TextField
              label="Phone"
              value={applicantPhone}
              onChange={(e) => setApplicantPhone(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Stack>

          <SmsConsentCheckbox
            checked={smsConsent}
            onChange={setSmsConsent}
            error={error?.toLowerCase().includes("sms consent") ? error : ""}
          />

          <FormControl>
            <RadioGroup
              value={resumeSource}
              onChange={(e) => {
                const v = e.target.value as ResumeSource;
                setResumeSource(v);
                if (v !== "existing") setForceClearResume(false);
              }}
            >
              <FormControlLabel
                value="existing"
                control={<Radio />}
                label="Use my saved resume"
                disabled={!savedResumeUrl}
              />
              <FormControlLabel value="link" control={<Radio />} label="Paste a resume link" />
              <FormControlLabel value="upload" control={<Radio />} label="Upload a file" />
            </RadioGroup>
          </FormControl>

          {showSavedRow && (
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Chip size="small" label="Saved resume" variant="outlined" />
              <Tooltip title="Open resume">
                <IconButton size="small" component="a" href={savedResumeUrl} target="_blank" rel="noopener noreferrer">
                  <OpenInNewRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button size="small" variant="text" onClick={removeSavedResume} startIcon={<DeleteOutlineRoundedIcon />}>
                Don’t use this resume
              </Button>
            </Stack>
          )}

          {resumeSource === "link" && (
            <TextField
              label="Resume URL (Drive/Dropbox/etc.)"
              value={resumeUrl}
              onChange={(e) => {
                setResumeUrl(e.target.value);
                setForceClearResume(false);
              }}
              placeholder="https://..."
            />
          )}

          {showUploadRow && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Attach resume (PDF/DOC/DOCX)
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <input
                  id="resume-file-input"
                  type="file"
                  accept={ACCEPT_MIME}
                  style={{ display: "none" }}
                  onChange={onPickFile}
                />
                <label htmlFor="resume-file-input">
                  <Button variant="outlined" size="small" startIcon={<UploadFileRoundedIcon />} component="span" disabled={loading}>
                    Choose file
                  </Button>
                </label>

                {file && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={file.name}
                    onDelete={loading ? undefined : removeFileSelection}
                    deleteIcon={<DeleteOutlineRoundedIcon />}
                  />
                )}

                {file && uploading && (
                  <Typography variant="caption" color="text.secondary">
                    Uploading… please wait
                  </Typography>
                )}

                {fileUrl && (
                  <>
                    <Tooltip title="Open uploaded file">
                      <IconButton size="small" component="a" href={fileUrl} target="_blank" rel="noopener noreferrer">
                        <OpenInNewRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      Uploaded — this file will be submitted
                    </Typography>
                  </>
                )}
              </Stack>
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {existingId ? (
          <Button variant="contained" onClick={handleUpdate} disabled={submitBlocked}>
            {updating || uploading ? "Please wait…" : "Update Application"}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSubmit} disabled={submitBlocked}>
            {submitting || uploading ? "Please wait…" : "Submit Application"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
